<app-kpi-details-dialog title="Detalle de coincidencia con compañeros" [subtitle]="subtitle()">
  <div class="info-card colleagues-card">
    <p class="card-label">
      Compañeros de producto
      <mat-icon
        class="info-icon-tooltip"
        matTooltip="Compañeros con los que compartes producto. Algunos pueden no aparecer si tienen una excepción activa de este KPI. Si un compañero está de vacaciones, ausente o en festivo, se muestra aquí pero ese día no entra en tu cálculo: no te penaliza ni te beneficia."
        matTooltipPosition="above"
        matTooltipShowDelay="300"
        matTooltipHideDelay="100"
        matTooltipClass="kpi-tooltip"
        >info</mat-icon
      >
    </p>
    <div class="colleague-chips">
      @for (colleague of colleagues(); track colleague) {
        <span class="colleague-chip">{{ colleague }}</span>
      }
    </div>
    <p class="info-note">
      <mat-icon class="info-icon">lightbulb</mat-icon>
      Se considera coincidencia cuando tú y tus compañeros estáis fichados a la vez. El objetivo es alcanzar al menos el
      {{ data.optimal_threshold | number: '1.0-0' }}% de coincidencia ponderada respecto a tu jornada de contrato.
    </p>
  </div>

  <div class="info-card status-card">
    <div class="status-grid">
      <div class="status-item">
        <span class="status-label">Estado actual:</span>
        <span
          class="status-value"
          [class.goal-met]="(current_stats()?.percentage ?? 0) >= data.optimal_threshold"
          [class.goal-not-met]="(current_stats()?.percentage ?? 0) < data.optimal_threshold"
          >{{ $safeNavigationMigration(current_stats()?.percentage) | number: '1.0-0' }}%</span
        >
        <span class="status-sub">{{ current_stats()?.worked_days }} días trabajados</span>
      </div>
      <div class="status-item">
        <span class="status-label">Previsión fin de mes:</span>
        <span class="status-value" [class.goal-met]="will_meet_goal()" [class.goal-not-met]="!will_meet_goal()"
          >{{ forecast_stats().percentage | number: '1.0-0' }}%</span
        >
        <span class="status-sub">{{ forecast_stats().total_days }} días totales</span>
      </div>
      <div class="status-item">
        <span class="status-label">Objetivo mínimo:</span>
        <span class="status-value goal">{{ data.optimal_threshold | number: '1.0-0' }}%</span>
        @if (will_meet_goal()) {
          <span class="status-sub goal-met">
            <mat-icon class="check-icon">check</mat-icon>
            Cumplirás objetivo
          </span>
        } @else {
          <span class="status-sub goal-not-met">
            <mat-icon class="warning-icon">warning</mat-icon>
            No cumplirás objetivo
          </span>
        }
      </div>
    </div>

    <div class="general-progress">
      <div class="progress-track">
        <div
          class="progress-fill"
          [style.width.%]="forecast_stats().percentage"
          [class.good]="will_meet_goal()"
          [class.poor]="!will_meet_goal()"
        ></div>
        <div class="threshold-marker" [style.left.%]="data.optimal_threshold"></div>
      </div>
      <div class="progress-labels">
        <span class="threshold-label">Aceptable: {{ data.acceptable_threshold }}% - Óptimo: {{ data.optimal_threshold }}%</span>
      </div>
    </div>
  </div>

  <h3 class="section-title">Días trabajados + Previsión</h3>

  @for (day of day_cards(); track day.date) {
    <div class="day-card" [class.forecast]="day.is_forecast" [style.border-color]="day.progress_bar_color">
      <div class="day-header">
        <div class="day-info">
          <span class="day-label">{{ day.day_label }}</span>
          @if (day.location_tooltip) {
            <span
              class="day-location-icons"
              [matTooltip]="day.location_tooltip"
              matTooltipPosition="above"
              matTooltipShowDelay="200"
              matTooltipClass="kpi-tooltip"
            >
              @if (day.has_remote_clock_registers) {
                <span class="location-icon" aria-label="Remoto">🏠</span>
              }
              @if (day.has_office_clock_registers) {
                <span class="location-icon" aria-label="Oficina">🏢</span>
              }
            </span>
          }
        </div>
        <span class="day-percentage" matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip"
          >{{ day.percentage | number: '1.0-0' }}%

          <mat-icon
            class="info-icon-tooltip"
            [matTooltip]="day.table_copy.day_percentage_tooltip"
            matTooltipPosition="above"
            matTooltipShowDelay="300"
            matTooltipHideDelay="100"
            matTooltipClass="kpi-tooltip"
            >info</mat-icon
          ></span
        >
      </div>

      <div class="day-progress">
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="day.percentage" [style.background-color]="day.progress_bar_color"></div>
        </div>
      </div>

      @if (day.colleagues.length === 0) {
        Sin compañeros de producto
      } @else {
        <div class="colleagues-table-wrapper">
          <table class="colleagues-table">
            <thead>
              <tr>
                <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                  Compañero
                  <mat-icon
                    class="info-icon-tooltip"
                    [matTooltip]="day.table_copy.colleague_column_tooltip"
                    matTooltipPosition="above"
                    matTooltipShowDelay="300"
                    matTooltipHideDelay="100"
                    matTooltipClass="kpi-tooltip"
                    >info</mat-icon
                  >
                </th>
                <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                  Peso
                  <mat-icon
                    class="info-icon-tooltip"
                    matTooltip="Proporción del tiempo esperado de este compañero en productos que compartís. Ej.: 0,50 = la mitad de su jornada en productos comunes. A más peso, más influye en tu % del día cuando coincidís."
                    matTooltipPosition="above"
                    matTooltipShowDelay="300"
                    matTooltipHideDelay="100"
                    matTooltipClass="kpi-tooltip"
                    >info</mat-icon
                  >
                </th>
                <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                  {{ day.table_copy.present_column_label }}
                  <mat-icon
                    class="info-icon-tooltip"
                    [matTooltip]="day.table_copy.present_column_tooltip"
                    matTooltipPosition="above"
                    matTooltipShowDelay="300"
                    matTooltipHideDelay="100"
                    matTooltipClass="kpi-tooltip"
                    >info</mat-icon
                  >
                </th>
                <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                  {{ day.table_copy.my_time_column_label }}
                  <mat-icon
                    class="info-icon-tooltip"
                    [matTooltip]="day.table_copy.my_time_column_tooltip"
                    matTooltipPosition="above"
                    matTooltipShowDelay="300"
                    matTooltipHideDelay="100"
                    matTooltipClass="kpi-tooltip"
                    >info</mat-icon
                  >
                </th>
                <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                  Coincid. potencial
                  <mat-icon
                    class="info-icon-tooltip"
                    [matTooltip]="day.table_copy.potential_coincidence_tooltip"
                    matTooltipPosition="above"
                    matTooltipShowDelay="300"
                    matTooltipHideDelay="100"
                    matTooltipClass="kpi-tooltip"
                    >info</mat-icon
                  >
                </th>
                @if (!day.is_forecast) {
                  <th matTooltipPosition="above" matTooltipShowDelay="200" matTooltipClass="kpi-tooltip">
                    Coincid. final
                    <mat-icon
                      class="info-icon-tooltip"
                      matTooltip="Horas reales en las que coincidisteis, multiplicadas por su peso. Siempre es igual o menor que Coincid. potencial."
                      matTooltipPosition="above"
                      matTooltipShowDelay="300"
                      matTooltipHideDelay="100"
                      matTooltipClass="kpi-tooltip"
                      >info</mat-icon
                    >
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (colleague of day.colleagues; track colleague.name) {
                <tr [class.colleague-absent]="!colleague.is_present">
                  <td>
                    {{ colleague.name }}
                    @if (colleague.absence_type === day_off_type.Vacation) {
                      <span
                        class="absence-emoji"
                        [matTooltip]="colleague.absence_tooltip ?? ''"
                        matTooltipPosition="above"
                        matTooltipShowDelay="200"
                        matTooltipClass="kpi-tooltip"
                        >🌴</span
                      >
                    } @else if (colleague.absence_type === day_off_type.Absence) {
                      <span
                        class="absence-emoji"
                        [matTooltip]="colleague.absence_tooltip ?? ''"
                        matTooltipPosition="above"
                        matTooltipShowDelay="200"
                        matTooltipClass="kpi-tooltip"
                        >🪑</span
                      >
                    } @else if (colleague.absence_type === day_off_type.Holiday) {
                      <span
                        class="absence-emoji"
                        [matTooltip]="colleague.absence_tooltip ?? ''"
                        matTooltipPosition="above"
                        matTooltipShowDelay="200"
                        matTooltipClass="kpi-tooltip"
                        >🎆</span
                      >
                    } @else if (colleague.absence_type === day_off_type.ExcusedRemote) {
                      <span
                        class="absence-emoji"
                        [matTooltip]="colleague.absence_tooltip ?? ''"
                        matTooltipPosition="above"
                        matTooltipShowDelay="200"
                        matTooltipClass="kpi-tooltip"
                        >🏠</span
                      >
                    }
                  </td>
                  <td>{{ colleague.weight | number: '1.2-2' }}</td>
                  <td>{{ colleague.is_present ? 'Sí' : 'No' }}</td>
                  <td>{{ day.my_hours | hourDecimalToHoursMinutes }}</td>
                  <td>{{ colleague.potential_coincidence_hours | hourDecimalToHoursMinutes }}</td>
                  @if (!day.is_forecast) {
                    <td>{{ colleague.coincided_hours | hourDecimalToHoursMinutes }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }

  <div class="info-card summary-card">
    <p class="card-label">Resumen con previsión</p>
    <div class="summary-grid">
      <div class="summary-item">
        <span class="summary-label">Total horas trabajadas:</span>
        <span class="summary-value">
          {{ summary_stats().worked_hours_total }}h
          @if (summary_stats().forecast_hours_total > 0) {
            <span class="summary-forecast"
              >(+ {{ summary_stats().forecast_hours_total }}h previstos = {{ summary_stats().total_hours }}h)</span
            >
          }
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Horas con coincidencia:</span>
        <span class="summary-value">
          {{ summary_stats().worked_hours_coincided }}h
          @if (summary_stats().forecast_hours_coincided > 0) {
            <span class="summary-forecast"
              >(+ {{ summary_stats().forecast_hours_coincided }}h previstos = {{ summary_stats().total_coincided }}h)</span
            >
          }
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-label">% Proyectado de coincidencia:</span>
        <span class="summary-value projected" [class.goal-met]="will_meet_goal()" [class.goal-not-met]="!will_meet_goal()"
          >{{ summary_stats().percentage | number: '1.0-0' }}%</span
        >
      </div>
    </div>
  </div>
</app-kpi-details-dialog>
